import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import SeoHead, { getArticleSeo } from "@/components/SeoHead";
import { ReadingSequenceNav } from "@/components/ReadingSeries";
import { articleRelationships, articles } from "@/data/siteData";
import { normalizeArticle } from "@/data/normalizeArticle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function preferEditorialSample(article) {
  return articles.find((item) => item.slug === article.slug) || normalizeArticle(article);
}

function ArticleBody({ article }) {
  const sections = article.sections || [{ heading: "", paragraphs: article.body }];
  return sections.map((section, index) => (
    <section className="article-section" data-testid={`article-section-${index + 1}`} key={section.heading || index}>
      {section.heading && <h2 className="article-section-title">{section.heading}</h2>}
      {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
    </section>
  ));
}

export default function ArticlePage() {
  const { slug } = useParams();
  const localArticle = articles.find((item) => item.slug === slug);
  const [article, setArticle] = useState(localArticle);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    setArticle(localArticle);
    setChecking(true);
    api.get(`${API}/articles/${slug}`).then(({ data }) => setArticle(preferEditorialSample(data))).catch(() => {}).finally(() => setChecking(false));
  }, [slug, localArticle]);

  if (!article && checking) return <main className="auth-state" data-testid="article-loading-state"><div className="shell"><p>Opening the note…</p></div></main>;
  if (!article) return <Navigate to="/insights" replace />;

  return (
    <>
      <SeoHead {...getArticleSeo(article, slug)} />
      <main data-testid={`article-page-${article.slug}`}>
      <section className="page-section">
        <div className="shell article-shell">
          <Reveal>
            <header className="article-header">
              <Link to="/insights" state={{ restoreInsights: true }} className="inline-link" data-testid="article-back-link"><ArrowLeft size={15} /> Back to insights</Link>
              <div className="meta-row" style={{ marginTop: 40 }}><span className="insight-category">{article.category}</span><span>{article.date} · {article.readingTime}</span></div>
              <h1 className="article-title" data-testid="article-title">{article.title}</h1>
              <p className="article-deck">{article.excerpt}</p>
            </header>
          </Reveal>
          <Reveal className="article-body" delay={120}>
            <div data-testid="article-body"><ArticleBody article={article} /></div>
            <aside className="article-takeaways" data-testid="article-takeaways"><h2>{article.takeawayHeading || "In brief"}</h2><ul>{article.takeaways.map((item) => <li key={item}>{item}</li>)}</ul></aside>
            {article.closing && <p className="article-closing" data-testid="article-closing">{article.closing}</p>}
            {articleRelationships[article.slug] && <RelatedReading article={article} />}
            <ReadingSequenceNav slug={article.slug} />
            <Link to="/connect" className="button-primary" style={{ marginTop: 38 }} data-testid="article-connect-link">{article.ctaLabel || "If this raises a question, get in touch"} <ArrowUpRight size={15} /></Link>
          </Reveal>
        </div>
      </section>
      </main>
    </>
  );
}

function RelatedReading({ article }) {
  const relation = articleRelationships[article.slug];
  const related = articles.find((item) => item.slug === relation.insight);
  return <div className="article-related" data-testid={`article-related-reading-${article.slug}`}><div className="section-kicker">If you want to follow the thread</div><div className="article-related-links"><Link to="/frameworks" data-testid={`article-related-framework-${article.slug}`}>{relation.framework} <ArrowUpRight size={13} /></Link>{related && <Link to={`/insights/${related.slug}`} data-testid={`article-related-insight-${article.slug}`}>{related.title} <ArrowUpRight size={13} /></Link>}<Link to={`/transformations#transformation-${relation.transformation}`} data-testid={`article-related-transformation-${article.slug}`}>Transformation pattern <ArrowUpRight size={13} /></Link></div></div>;
}